import { useAtomValue } from 'jotai';

import {
  CentralSessionControls,
  LeftsideSessionControls,
  RightsideSessionControls,
  SessionControl,
  SessionControlsLayout,
} from '../../components';
import { ToolbarSeparator } from '../../components/ToolbarSeparator';
import { memoize } from '../../utils';
import { participantCountAtom } from '../../video-sdk';
import { useObscuredProviderContentProps } from '../a11y';
import { ClientsDrawerButtonWrapper as ClientsDrawerButton } from '../clients-drawer';
import { CompleteSessionButton } from '../complete-session/CompleteSessionButton';
import { RecordingControl } from '../session-recording/RecordingControl';
import { ChatDrawerControlMenuItemWrapper as ChatDrawerControlMenuItem } from './ChatDrawerControlMenuItem';
import { ClientInformationDrawerControlButtonWrapper as ClientInformationDrawerControlButton } from './ClientInformationDrawerControlButton';
import { ClientInformationDrawerControlMenuItemWrapper as ClientInformationDrawerControlMenuItem } from './ClientInformationDrawerControlMenuItem';
import { ClientListDrawerControlMenuItemWrapper as ClientListDrawerControlMenuItem } from './ClientListDrawerControlMenuItem';
import { PatientNotePopoverControlButtonWrapper as PatientNotePopoverControlButton } from './PatientNotePopoverControlButton';
import { ProviderAdditionalOptionsMenuWrapper as ProviderAdditionalOptionsMenu } from './ProviderAdditionalOptionsMenu';
import { ProviderAudioControlWrapper as ProviderAudioControl } from './ProviderAudioControl';
import { ProviderChatDrawerControlButtonWrapper as ProviderChatDrawerControlButton } from './ProviderChatDrawerControlButton';
import {
  ProviderSettingsDrawerButton,
  ProviderSettingsDrawerControlMenuItemWrapper as ProviderSettingsDrawerControlMenuItem,
} from './ProviderSettingsDrawerControlsWrapper';
import { ProviderVideoControlWrapper as ProviderVideoControl } from './ProviderVideoControl';
import { SessionModeControlButtonWrapper as SessionModeControlButton } from './SessionModeControlButton';
import { SessionModeControlMenuItemsWrapper as SessionModeControlMenuItems } from './SessionModeControlMenuItems';

const inSessionCentralControls: SessionControl[] = [
  {
    key: 'client-list',
    additionalOptionsMenuItemComponent: ClientListDrawerControlMenuItem,
  },
  {
    key: 'session-mode',
    toolbarComponent: SessionModeControlButton,
    additionalOptionsMenuItemComponent: SessionModeControlMenuItems,
  },
  {
    key: 'client-information',
    toolbarComponent: ClientInformationDrawerControlButton,
    additionalOptionsMenuItemComponent: ClientInformationDrawerControlMenuItem,
  },
  {
    key: 'patient-note',
    toolbarComponent: PatientNotePopoverControlButton,
  },
  {
    key: 'session-tools-separator',
    toolbarComponent: ToolbarSeparator,
  },
  {
    key: 'recording',
    toolbarComponent: RecordingControl,
  },
];

const getCentralControlsMemoized = memoize((isInSession: boolean): SessionControl[] => {
  return [
    {
      key: 'audio',
      toolbarComponent: ProviderAudioControl,
    },
    {
      key: 'video',
      toolbarComponent: ProviderVideoControl,
    },
    {
      key: 'av-separator',
      toolbarComponent: ToolbarSeparator,
    },
    {
      key: 'chat',
      toolbarComponent: ProviderChatDrawerControlButton,
      additionalOptionsMenuItemComponent: ChatDrawerControlMenuItem,
    },
    ...(isInSession ? inSessionCentralControls : []),
    {
      key: 'settings',
      toolbarComponent: ProviderSettingsDrawerButton,
      additionalOptionsMenuItemComponent: ProviderSettingsDrawerControlMenuItem,
    },
  ];
});

export function ProviderSessionControlsWrapper() {
  const obscuredContentProps = useObscuredProviderContentProps();
  const isInSession = !!useAtomValue(participantCountAtom);

  return (
    <SessionControlsLayout {...obscuredContentProps} className='order-3'>
      <LeftsideSessionControls>
        <ClientsDrawerButton />
      </LeftsideSessionControls>
      <CentralSessionControls
        controls={getCentralControlsMemoized(isInSession)}
        additionalOptionsMenuComponent={ProviderAdditionalOptionsMenu}
      />
      {isInSession && (
        <RightsideSessionControls>
          <CompleteSessionButton />
        </RightsideSessionControls>
      )}
    </SessionControlsLayout>
  );
}
